import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { CalendarIcon, ClockIcon, EditIcon, SaveIcon, XIcon } from 'lucide-react';
import type { CalendarDay, ScheduleType } from '@/types/schedule.types';

interface DayDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: CalendarDay | null;
  templateName: string;
  onUpdate?: (dayId: string, updates: Partial<CalendarDay>) => void;
  isReadOnly?: boolean;
}

const scheduleTypes: { value: ScheduleType; label: string; color: string }[] = [
  { value: 'working', label: 'Рабочий день', color: 'bg-green-100 text-green-900' },
  { value: 'day_off', label: 'Выходной', color: 'bg-gray-100 text-gray-600' },
  { value: 'holiday', label: 'Праздник', color: 'bg-red-100 text-red-900' },
  { value: 'reduced', label: 'Сокращённый', color: 'bg-amber-100 text-amber-900' }
];

export const DayDetailsModal: React.FC<DayDetailsModalProps> = ({
  isOpen,
  onClose,
  day,
  templateName,
  onUpdate,
  isReadOnly = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<CalendarDay>>({});

  React.useEffect(() => {
    if (day) {
      setEditData({
        shift_start: day.shift_start || '',
        shift_end: day.shift_end || '',
        schedule_type: day.schedule_type,
        is_working_day: day.is_working_day
      });
    }
  }, [day]);

  if (!day) return null;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCalendarTime = (time: string | null | undefined) => {
    if (!time) return '—';
    const value = time.toString();
    const timePart = value.includes('T') ? value.split('T')[1] : value;
    return timePart.slice(0, 5); // HH:mm
  };

  const getScheduleTypeInfo = (type: ScheduleType) => {
    return scheduleTypes.find(t => t.value === type) || scheduleTypes[0];
  };

  const handleSave = () => {
    if (day && onUpdate) {
      onUpdate(day.id, editData);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      shift_start: day.shift_start || '',
      shift_end: day.shift_end || '',
      schedule_type: day.schedule_type,
      is_working_day: day.is_working_day
    });
  };

  const scheduleTypeInfo = getScheduleTypeInfo(day.schedule_type);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Детали дня
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Дата */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <div>
              <p className="font-medium">{formatDate(day.date)}</p>
              <p className="text-sm text-gray-500">Шаблон: {templateName}</p>
            </div>
          </div>

          {/* Тип дня */}
          <div className="space-y-2">
            <Label>Тип дня</Label>
            {isEditing ? (
              <Select
                value={editData.schedule_type}
                onValueChange={(value: ScheduleType) =>
                  setEditData(prev => ({
                    ...prev,
                    schedule_type: value,
                    is_working_day: value === 'working' || value === 'reduced'
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scheduleTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${type.color.split(' ')[0]}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Badge className={scheduleTypeInfo.color}>
                {scheduleTypeInfo.label}
              </Badge>
            )}
          </div>

          {/* Рабочее время */}
          {(day.is_working_day || editData.schedule_type === 'working' || editData.schedule_type === 'reduced') && (
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <ClockIcon className="h-4 w-4" />
                Рабочие часы
              </Label>

              {isEditing ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Начало</Label>
                    <Input
                      type="time"
                      value={editData.shift_start || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, shift_start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Окончание</Label>
                    <Input
                      type="time"
                      value={editData.shift_end || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, shift_end: e.target.value }))}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-blue-600" />
                  <span className="font-mono">
                    {formatCalendarTime(day.shift_start)} — {formatCalendarTime(day.shift_end)}
                  </span>
                  {day.shift_start && day.shift_end && (
                    <span className="text-xs text-gray-500 ml-auto">
                      {(() => {
                        const start = new Date(`2000-01-01T${day.shift_start}`);
                        const end = new Date(`2000-01-01T${day.shift_end}`);
                        const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                        return `${diff}ч`;
                      })()}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Статус данных */}
          <div className="text-xs text-gray-500 pt-2 border-t">
            {day.is_generated ? (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                Сгенерировано из шаблона
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full" />
                Данные из календаря
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <XIcon className="h-4 w-4 mr-1" />
                Отмена
              </Button>
              <Button size="sm" onClick={handleSave}>
                <SaveIcon className="h-4 w-4 mr-1" />
                Сохранить
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onClose}>
                Закрыть
              </Button>
              {!isReadOnly && onUpdate && (
                <Button size="sm" onClick={() => setIsEditing(true)}>
                  <EditIcon className="h-4 w-4 mr-1" />
                  Редактировать
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};